
import {
  SelectChangeEvent,
} from '@mui/material'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useDepositNetworks } from '@/hooks/use-deposit-networks'
import useNemoWallet from '@/hooks/use-nemo-wallet'
import useToastMessage from '@/hooks/use-toast-message'
import useTrans from '@/hooks/useTrans'

import { WrappedTokensFlowContext } from '../context'
import { NetworkOption } from './types'
import { generateDepositAddresses, renecNetworkOption } from './utils'
import View from './view'

const Container = ({ onNext }: { onNext: () => void }) => {
  const trans = useTrans()
  const { showMessage } = useToastMessage()
  const timeoutRef = useRef<NodeJS.Timer | null>(null)
  const { publicKey } = useNemoWallet()
  const [isLoading, setIsLoading] = useState(false)
  const [isOpenMenu, setIsOpenMenu] = useState(false)
  const [addressToGenQR, setAddressToGenQR] = useState('')
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkOption>()
  const { selectedToken, setSelectedToken, tokenOptions } = useContext(WrappedTokensFlowContext)
  const { data: depositNetworks } = useDepositNetworks()

  useEffect(() => {
    if (!publicKey || !selectedToken) return
    if (!selectedNetwork) return
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (selectedNetwork.chain_name === renecNetworkOption.chain_name) {
      setAddressToGenQR(publicKey.toString())
      return
    }

    setIsLoading(true)
    timeoutRef.current = setTimeout(async () => {
      const response = await generateDepositAddresses(publicKey.toString(), selectedNetwork)

      if (!response || !response.address) {
        showMessage({
          txid: '',
          type: 'error',
          title: trans.common.failed,
          message: trans.common.something_wrong,
        })
      } else {
        setAddressToGenQR(response.address)
      }

      setIsLoading(false)
    }, 300)
  }, [publicKey, selectedNetwork, selectedToken, showMessage, trans])

  useEffect(() => {
    if (!publicKey || !selectedToken) return
    if (selectedToken.symbol === 'RENEC') {
      setAddressToGenQR(publicKey.toString())
      setSelectedNetwork(renecNetworkOption)
    }
  }, [selectedToken, publicKey])

  const networkOptions = useMemo(() => {
    let filteredDepositNetworks: NetworkOption[] = []

    if (selectedToken?.displaySymbol && depositNetworks) {
      filteredDepositNetworks = depositNetworks[selectedToken.displaySymbol.toLowerCase()] as NetworkOption[]
    }

    return [renecNetworkOption].concat(filteredDepositNetworks).filter(Boolean)
  }, [depositNetworks, selectedToken])

  const onChangeToken = useCallback((evt: SelectChangeEvent<string>) => {
    const token = tokenOptions.find(tokenOpt => tokenOpt.symbol === evt.target.value)
    if (token) {
      setSelectedToken(token)
    }
  }, [setSelectedToken, tokenOptions])

  const onChangeNetwork = useCallback((network: NetworkOption) => {
    setSelectedNetwork(network)
    setIsOpenMenu(false)
  }, [])

  if (!selectedToken) return null

  const injectedProps = {
    selectedToken,
    selectedNetwork,
    onChangeToken,
    isOpenMenu,
    setIsOpenMenu,
    networkOptions,
    isLoading,
    onChangeNetwork,
    addressToGenQR,
    onNext,
    tokenOptions,
  }

  return (
    <View {...injectedProps} />
  )
}

export default Container
